import {
  CostReimbursementByPharmacist,
  CostReimbursementSummaryByPharmacist,
  PaginationCriteriaInput,
  PharmacyCostReimbursementRequestInput,
  PharmacyCostReimbursementSummaryRequestInput
} from '@/api/bff_types';
import useSettlementQuery from '@/hooks/settlement/useSettlementQuery';
import {
  VarianceService,
  VarianceSummaryService
} from '@/services/settlements/interface';
import { formatComma, parseVarianceReason } from '@/utils/form';
import { formattedDate } from '@/utils/time';

const getVarianceForPharmacistData = (
  response?: CostReimbursementByPharmacist
): VarianceService => {
  if (!response?.costReimbursementByPharmacist)
    return { costReimbursements: [] };
  return {
    costReimbursements: response.costReimbursementByPharmacist.map(
      costReimbursement => ({
        createdAt: formattedDate(costReimbursement.createdAt, 'YYYY.MM.DD'),
        patientName: costReimbursement.order?.forms?.[0]?.patientName ?? '',
        varianceReason: parseVarianceReason(costReimbursement.reason),
        varianceAmount: `${
          costReimbursement.costReimbursementType === 'DEDUCTION' ? '-' : ''
        }${formatComma(costReimbursement.amount)} 원`,
        cardCommission: formatComma(costReimbursement.fee) + '원',
        totalAmount: `${
          costReimbursement.costReimbursementType === 'DEDUCTION' ? '-' : ''
        }${formatComma(costReimbursement.calculatedAmount)} 원`,
        orderExternalId: costReimbursement.order.orderExternalId,
        id: Number(costReimbursement.orderId) ?? 0,
        orderId: costReimbursement.order.orderId,
        costReimbursementId: Number(costReimbursement.costReimbursementId) ?? 0
      })
    )
  };
};

const getVarianceSummaryForPharmacistData = (
  response?: CostReimbursementSummaryByPharmacist
): VarianceSummaryService => {
  if (!response?.costReimbursementSummaryByPharmacist)
    return {
      costReimbursementSummary: {
        count: 0,
        reimbursementSum: 0,
        reimbursementSumWon: '0 원'
      }
    };
  return {
    costReimbursementSummary: {
      count: response.costReimbursementSummaryByPharmacist?.count ?? 0,
      reimbursementSum:
        response.costReimbursementSummaryByPharmacist?.reimbursementSum ?? 0,
      reimbursementSumWon:
        formatComma(
          response.costReimbursementSummaryByPharmacist?.reimbursementSum ?? 0
        ) + ' 원'
    }
  };
};

const getVarianceByPharmacyService = (
  request: PharmacyCostReimbursementRequestInput,
  paginationCriteria: PaginationCriteriaInput
) => {
  const { settlementVarianceListQuery } = useSettlementQuery();

  const { data: varianceSummaryData } = settlementVarianceListQuery(
    { ...request },
    { ...paginationCriteria }
  );

  return getVarianceForPharmacistData(varianceSummaryData);
};

const getVarianceSummaryByPharmacyService = (
  request: PharmacyCostReimbursementSummaryRequestInput
) => {
  const { settlementVarianceSummaryQuery } = useSettlementQuery();

  const { data: varianceSummaryData } = settlementVarianceSummaryQuery({
    ...request
  });

  return getVarianceSummaryForPharmacistData(varianceSummaryData);
};

export default {
  getVarianceByPharmacyService,
  getVarianceSummaryByPharmacyService
};
